import React from 'react';

export interface ToggleProps {
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
    label?: React.ReactNode;
    description?: React.ReactNode;
    id?: string;
    className?: string;
    onClick?: (e: React.MouseEvent) => void;
    color?: 'emerald' | 'primary' | 'blue' | 'indigo';
    size?: 'sm' | 'md' | 'lg';
    reverse?: boolean;
    card?: boolean;
}

export default function Toggle({
    checked,
    onChange,
    disabled = false,
    label,
    description,
    id,
    className = '',
    onClick,
    color = 'emerald',
    size = 'md',
    reverse = false,
    card = false,
}: ToggleProps) {
    const handleToggle = (e?: React.MouseEvent) => {
        if (disabled) return;
        if (e && onClick) {
            onClick(e);
        }
        onChange(!checked);
    };

    // Configurações dimensionais do switch
    const sizeConfig = {
        sm: {
            track: 'h-5 w-9',
            knob: 'h-4 w-4',
            translate: 'translate-x-4',
        },
        md: {
            track: 'h-6 w-11',
            knob: 'h-5 w-5',
            translate: 'translate-x-5',
        },
        lg: {
            track: 'h-7 w-14',
            knob: 'h-6 w-6',
            translate: 'translate-x-7',
        },
    }[size];

    // Paleta de cores ativas
    const activeColorMap = {
        emerald: 'bg-emerald-600 focus:ring-emerald-500',
        primary: 'bg-primary-600 focus:ring-primary-500',
        blue: 'bg-blue-600 focus:ring-blue-500',
        indigo: 'bg-indigo-600 focus:ring-indigo-500',
    };
    const activeColor = activeColorMap[color] || activeColorMap.emerald;

    // Paleta de cards ativos
    const activeCardBgMap = {
        emerald: 'bg-emerald-50/40 border-emerald-300 shadow-xs',
        primary: 'bg-primary-50/40 border-primary-300 shadow-xs',
        blue: 'bg-blue-50/50 border-blue-300 shadow-xs',
        indigo: 'bg-indigo-50/50 border-indigo-300 shadow-xs',
    };
    const activeCardBg = activeCardBgMap[color] || activeCardBgMap.emerald;

    // Botão switch acessível e resistente a deformações
    const switchButton = (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            disabled={disabled}
            id={id}
            onClick={(e) => {
                e.stopPropagation();
                handleToggle(e);
            }}
            className={`relative inline-flex ${sizeConfig.track} shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-offset-2 ${
                checked ? activeColor : 'bg-slate-300'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
            <span
                aria-hidden="true"
                className={`pointer-events-none inline-block ${sizeConfig.knob} transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${
                    checked ? sizeConfig.translate : 'translate-x-0'
                }`}
            />
        </button>
    );

    // Variante 1: Card de Configuração do Sistema
    if (card) {
        return (
            <div
                role="button"
                tabIndex={disabled ? -1 : 0}
                onClick={handleToggle}
                onKeyDown={(e) => {
                    if (e.key === ' ' || e.key === 'Enter') {
                        e.preventDefault();
                        handleToggle();
                    }
                }}
                className={`p-4 rounded-xl border transition-all flex items-start justify-between gap-4 cursor-pointer select-none ${
                    checked ? activeCardBg : 'bg-slate-50 border-slate-200 hover:border-slate-300'
                } ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
            >
                <div className="flex flex-col">
                    {label && <span className="text-sm font-semibold text-gray-900">{label}</span>}
                    {description && <p className="text-xs text-gray-500 mt-1 leading-relaxed">{description}</p>}
                </div>
                {switchButton}
            </div>
        );
    }

    // Variante 2: Layout com Switch à Direita (reverse)
    if (reverse) {
        return (
            <div
                onClick={handleToggle}
                className={`flex items-start justify-between gap-3 cursor-pointer select-none ${
                    disabled ? 'opacity-50 cursor-not-allowed' : ''
                } ${className}`}
            >
                <div className="flex flex-col">
                    {label && <span className="text-sm font-semibold text-gray-900">{label}</span>}
                    {description && <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{description}</p>}
                </div>
                {switchButton}
            </div>
        );
    }

    // Variante 3: Inline com Label / Descrição
    if (label || description) {
        return (
            <div
                onClick={handleToggle}
                className={`inline-flex items-start gap-3 cursor-pointer select-none ${
                    disabled ? 'opacity-50 cursor-not-allowed' : ''
                } ${className}`}
            >
                <div className="pt-0.5 shrink-0">
                    {switchButton}
                </div>
                <div className="flex flex-col">
                    {label && <span className="text-sm font-medium text-gray-700">{label}</span>}
                    {description && <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{description}</p>}
                </div>
            </div>
        );
    }

    // Variante 4: Apenas o Switch Isolado
    return (
        <div className={`inline-flex items-center ${className}`}>
            {switchButton}
        </div>
    );
}
